import type { AbsenceTypeInput, B2BContractType } from '@virtuslab/nfs-shared/src/schema/admin';
import type {
  CalendarDayType,
  ContractStatus,
  EmploymentContractType,
  Settlement,
  AbsenceType,
  OnCallMultiplier,
} from '@virtuslab/nfs-shared/src/schema/user';
import type { InterpolatedVariables, PluralBuildingBlocksToMessages, SharedEnumMessages } from '@virtuslab/nfs-shared/src/services/i18n/messages';
import {
  sharedPluralBuildingBlocks,
  makePluralMessages, sharedPluralMessages,
  sharedMessages,
} from '@virtuslab/nfs-shared/src/services/i18n/messages';

export interface EnumMessages extends SharedEnumMessages {
  contractStatus: ContractStatus;
  employmentContractType: EmploymentContractType;
  settlement: Settlement;
  b2bContractType: B2BContractType;
  calendarDayType: CalendarDayType;
  absenceType: AbsenceType;
  absenceTypeInput: AbsenceTypeInput;
  onCallMultiplier: OnCallMultiplier;
}

export const enumMessage = <T extends keyof EnumMessages, U extends EnumMessages[T]>(
  enumName: T,
  value: U,
): `${T}-${U}` => `${enumName}-${value}` as const;

export type Messages = {
  [key in keyof EnumMessages as `${key}-${EnumMessages[key]}`]: undefined
} & {
  [key in PluralBuildingBlocksToMessages<typeof pluralBuildingBlocks>]: InterpolatedVariables<key>
} & {
  [key in PluralMessages]: InterpolatedVariables<key>
} & {
  [key in typeof messages[number]]: InterpolatedVariables<key>
};

export type MessageKey = keyof Messages;
type PluralMessages = keyof (typeof sharedPluralMessages);

export const pluralMessages = {
  ...sharedPluralMessages,
  ...makePluralMessages({}),
};

export const pluralBuildingBlocks = {
  ...sharedPluralBuildingBlocks,
  ...makePluralMessages({}),
};

const messages = [
  ...sharedMessages,
  'No Luminis products to show',
  'No access to {{thing}}',
  'You have no signed contract in the system. Contact your Space Owner',
  'Drawer with a form for editing time report preferences',
  'Default values in a new entry',
  'Selected values will be prefilled each time you add a new entry',
  'If a project is not available on the selected date, you will have to pick it manually',
  'NFS Guide',
  'NFS feedback (Slack)',
  'No operational lead assigned. Please contact your Space Owner.',
  'No entries to show',
  'Search employees',
  'My profile',
  'Synced from Slack — managed automatically',
  'ID card',
  'Passport',
  'To update your residence address, please submit a written request to the Finance Department.',
  'You don\'t have any contracts',
  'Progression doesn\'t apply to any of your contracts',
  'Drawer with a form for booking a desk',
  'Drawer with a form for booking parking',
  'Permanent desk',
  'Guests: {{number}}',
  'Waiting for approval',
  'My bookings',
  'Cancel this booking?',
  'No offices are open for booking',
  'Office & Spots',
  'Book a desk or a parking spot in any office. Some bookings need approval from the office team.',
  '{{office}} is blocked. Nothing can be booked there until the office team unblocks it.',
  '{{office}} (blocked)',
] as const;

export default messages;
